// Dashboard.jsx
import React, { useEffect, useState } from 'react';
import { 
  Clock, 
  Loader2, 
  CheckCircle2, 
  Package, 
  AlertTriangle,
  RefreshCw,
  Eye
} from 'lucide-react';
import axios from 'axios';
import config from '../../config';
import { Link } from 'react-router-dom';

export default function Dashboard() {
  const token = localStorage.getItem("accessToken");

  const [stats, setStats] = useState({ toBreak: 0, inProgress: 0, completed: 0 });
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const fetchData = async (isRefresh = false) => {
    if (!token) {
      setError("Session expirée. Veuillez vous reconnecter.");
      setLoading(false);
      return;
    }

    if (isRefresh) setRefreshing(true);
    else setLoading(true);

    setError(null);

    try {
      const [statsRes, ordersRes] = await Promise.all([
        axios.get(`${config.REACT_APP_API_URL}/countorderbyuser`, {
          headers: { 
            Authorization: `Bearer ${token}`,
            'x-api-key': config.SITE_API_KEY
          },
        }),
        axios.get(`${config.REACT_APP_API_URL}/getorderwithoutsubscription`, {
          headers: { 
            Authorization: `Bearer ${token}`,
            'x-api-key': config.SITE_API_KEY
          },
        })
      ]);

      setStats({
        toBreak: statsRes.data.tobreak || 0,
        inProgress: statsRes.data.inprogress || 0,
        completed: statsRes.data.toend || 0,
      });

      setOrders(ordersRes.data.result || []);
    } catch (err) {
      console.error(err);
      setError("Erreur lors du chargement des données");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [token]);

  // Formatage de date simple sans bibliothèque externe
  const formatDate = (dateString) => {
    if (!dateString) return '—';
    
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '—';

    const day = String(date.getDate()).padStart(2, '0');
    const months = [
      'janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin',
      'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.'
    ];
    const month = months[date.getMonth()];
    const year = date.getFullYear();

    return `${day} ${month} ${year}`;
  };

  const statCards = [
    {
      title: "En attente",
      value: stats.toBreak,
      icon: Clock,
      color: "text-amber-600 dark:text-amber-400",
      bg: "bg-amber-50/70 dark:bg-amber-950/40",
      accent: "border-l-4 border-amber-500"
    },
    {
      title: "En cours",
      value: stats.inProgress,
      icon: Loader2,
      color: "text-blue-600 dark:text-blue-400",
      bg: "bg-blue-50/70 dark:bg-blue-950/40",
      accent: "border-l-4 border-blue-500"
    },
    {
      title: "Terminées",
      value: stats.completed,
      icon: CheckCircle2,
      color: "text-emerald-600 dark:text-emerald-400",
      bg: "bg-emerald-50/70 dark:bg-emerald-950/40",
      accent: "border-l-4 border-emerald-500"
    }
  ];

  const getStatusBadge = (status) => {
    const s = (status || '').toLowerCase();
    if (s.includes('terminée') || s.includes('terminé')) {
      return { bg: 'bg-emerald-100 dark:bg-emerald-900/50', text: 'text-emerald-800 dark:text-emerald-300', label: 'Terminée' };
    }
    if (s.includes('en cours')) {
      return { bg: 'bg-blue-100 dark:bg-blue-900/50', text: 'text-blue-800 dark:text-blue-300', label: 'En cours' };
    }
    if (s.includes('en attente')) {
      return { bg: 'bg-amber-100 dark:bg-amber-900/50', text: 'text-amber-800 dark:text-amber-300', label: 'En attente' };
    }
    return { bg: 'bg-gray-100 dark:bg-gray-800', text: 'text-gray-700 dark:text-gray-300', label: status || '—' };
  };

  return (
    <div className="space-y-8 pb-12">
      {/* Header */}
      <header className="flex flex-col min-sm:flex-row min-sm:items-center min-sm:justify-between gap-6">
        <div>
          <h1 className="text-xxl font-bold text-gray-900 dark:text-white tracking-tight">
            Tableau de bord
          </h1>
        </div>
      </header>

      {/* Cartes statistiques */}
      <section className="grid grid-cols-1 min-md:grid-cols-3 gap-5 lg:gap-6">
        {loading ? (
          statCards.map((_, i) => (
            <div
              key={i}
              className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 p-6 animate-pulse"
            >
              <div className="flex justify-between">
                <div className="space-y-3 w-2/3">
                  <div className="h-4 w-32 bg-gray-200 dark:bg-gray-700 rounded"></div>
                  <div className="h-10 w-20 bg-gray-200 dark:bg-gray-700 rounded"></div>
                </div>
                <div className="h-14 w-14 rounded-xl bg-gray-200 dark:bg-gray-700"></div>
              </div>
            </div>
          ))
        ) : (
          statCards.map((card, i) => (
            <div
              key={i}
              className={`
                relative rounded-2xl border p-6
                dark:bg-gray-900
                border-gray-200 dark:border-gray-800
                ${card.accent}
                hover:shadow-lg hover:scale-[1.015] transition-all duration-300
                group
              `}
            >
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                    {card.title}
                  </p>
                  <p className={`text-4xl font-bold ${card.color}`}>
                    {card.value.toLocaleString('fr-TN')}
                  </p>
                </div>
                <div className={`p-3.5 rounded-xl ${card.bg} group-hover:scale-110 transition-transform`}>
                  <card.icon className={`h-8 w-8 ${card.color}`} strokeWidth={2} />
                </div>
              </div>
            </div>
          ))
        )}
      </section>

      {/* Liste des commandes */}
      <section className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between dark:bg-gray-800">
          <div className="flex items-center gap-3">
            <div className="bg-emerald-50 dark:bg-emerald-950/50 rounded-lg">
              <Package className="h-6 w-6 text-emerald-600 dark:text-emerald-400" />
            </div>
            <h2 className="min-sm:text-xl text-lg font-semibold text-gray-900 dark:text-white mb-0">
              Commandes en attente de traitement
            </h2>
          </div>
        </div>

        {orders.length === 0 ? (
          <div className="py-20 px-6 text-center">
            <Package className="h-20 w-20 mx-auto text-gray-300 dark:text-gray-700 mb-6" strokeWidth={1} />
            <h3 className="text-xl font-semibold text-gray-700 dark:text-gray-300 mb-3">
              Aucune commande en attente
            </h3>
            <p className="text-gray-500 dark:text-gray-400 max-w-lg mx-auto">
              Les commandes sans abonnement apparaîtront ici dès qu'elles seront enregistrées.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th className="px-6 py-4 text-center text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">N°</th>
                  <th className="px-6 py-4 text-center text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">N° Commande</th>
                  <th className="px-6 py-4 text-center text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">État</th>
                  <th className="px-6 py-4 text-center text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Date</th>
                  <th className="px-6 py-4 text-center text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Détails</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
                {orders.map((order, i) => {
                  const badge = getStatusBadge(order.etat);
                  return (
                    <tr 
                      key={order._id || i}
                      className="hover:bg-gray-50 text-center dark:hover:bg-gray-800/40 transition-colors group dark:bg-gray-800"
                    >
                      <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-white">{i + 1}</td>
                      <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-white">{order.code_orders || '—'}</td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex px-3.5 py-1 text-xs font-medium rounded-full ${badge.bg} ${badge.text}`}>
                          {badge.label}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600 dark:text-white">
                        {formatDate(order.date)}
                      </td>
                      <td className="px-6 py-4 text-center">
                        <Link to={`/order-details-client/${order._id}`} className="text-sm font-medium text-gray-900 dark:text-white">
                          <Eye className="inline h-6 w-6 ml-1" />
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}